import { SOURCEAPP_ECHANNEL } from '@/constants';
import { getCountryCode } from '@/helpers/CountryCodeHelper';
import { store } from '@/store';
import { AddressDetail, BankAccountDetail } from '@/types/InfoState';
import { PersonalDetail } from '@/types/InfoState/PersonalDetail';
import {
  CreateProposalRequest,
  InforcePolicyRequest,
  ProposerData,
  RetrieveProposalRequest,
} from '@/types/Proposal';
import { getAPIPath } from '@/utility/common';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { add, format } from 'date-fns';
import axiosInterceptorInstance from './axiosInterceptorInstance';

export const inforcePolicy = createAsyncThunk(
  'application/InforcePolicy',
  async (encodedOrderId: string) => {
    try {
      const request: InforcePolicyRequest = {
        encodedOrderId,
        sendEmail: true,
        sendSMS: true,
        countryCode: getCountryCode(),
      };
      const relativeUrl = 'InforcePolicy';
      const apiUrl = `/application/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const retrievePaProposal = createAsyncThunk(
  'application/Pa/RetrieveProposal',
  async (request: RetrieveProposalRequest) => {
    const rootState = store.getState();
    const uiState = rootState.uiState;
    try {
      const relativeUrl = `${getAPIPath()}/RetrieveProposal`;
      const apiUrl = `/application/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      if (response.data.succeeded) response.data.data.encodedOrderId = request.encodedOrderId;
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const createPaProposal = createAsyncThunk(
  'application/Pa/CreateProposal',
  async ({
    language,
    productCode,
    entityType,
  }: {
    language: string;
    productCode: string;
    entityType: number;
  }) => {
    const rootState = store.getState();
    const planSummary = rootState.planSummary;
    const info = rootState.infoState;
    const affiliateState = rootState.affiliate;
    const uiState = rootState.uiState;
    const pv = planSummary.processVehicle;

    try {
      const relativeUrl = `${getAPIPath()}/CreateProposal`;
      const apiUrl = `/application/?relativeUrl=${relativeUrl}`;

      const today = new Date();
      const oneYearMinusOneDay = add(today, { years: 1, days: -1 });
      const todayFormatted = format(today, 'dd/MM/yyyy');
      const expiryDateFormatted = format(oneYearMinusOneDay, 'dd/MM/yyyy');

      const request: CreateProposalRequest = {
        countryCode: getCountryCode(),
        productCode,
        planClass: planSummary.quotationDetail ? planSummary.quotationDetail.planType : 0,
        startDate: todayFormatted,
        endDate: expiryDateFormatted,
        proposer: getProposer(info.personalDetail, info.bankAccountDetail, info.homeAddressDetail),
        vehicle: {
          registrationNo: pv ? pv.registrationNo : '',
          mmCode: info.vehicleDetail.mmCode,
          make: !planSummary.processVehicle?.hasNvic ? info.vehicleDetail.vehicleBrand : undefined,
          model: !planSummary.processVehicle?.hasNvic ? info.vehicleDetail.vehicleModel : undefined,
          chassisNo: info.vehicleDetail.chassisNumber,
          vehicleDescription: !planSummary.processVehicle?.hasNvic
            ? info.vehicleDetail.vehicleVariantDesc
            : undefined,
        },
        agentCode: affiliateState.agentCode,
        bankId: info.bankAccountDetail.bankId,
        accountHolder: info.bankAccountDetail.accountHolder,
        accountNo: info.bankAccountDetail.accountNo,
        quotationNo: pv ? pv.quotationNumber : '',
        referral: '',
        language,
        marketingConsent: planSummary.marketingConsent,
        declaration: planSummary.declaration,
        isStaff: affiliateState.isStaff ?? false,
        isPartner: false,
        clientPfNumber: affiliateState.clientPfNumber ?? '',
        checkPremium: planSummary.quotationDetail
          ? planSummary.quotationDetail?.planPremium.totalPremium
          : 0,
        entity: entityType,
        isEtiqa: pv ? pv.isEtiqa : false,
        previousInsurerCode: pv ? pv.previousInsurerCode : '',
        hasNvic: pv ? pv.hasNvic : false,
        affiliate: {
          affiliateId: affiliateState.agentId,
        },
        sourceAppId: SOURCEAPP_ECHANNEL,
      };
     

      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

const getProposer = (
  data: PersonalDetail,
  bankData: BankAccountDetail,
  mailingData: AddressDetail,
): ProposerData => {
  return {
    type: 1,
    salutation: '',
    name: data.name,
    lastName: '',
    identityType: data.idType,
    identityNumber: data.idNumber.toString(),
    nationality: data.nationalityId ?? 0,
    email: data.email,
    mobile: data.mobileNo.includes('*') ? '' : data.mobileNo,
    home: '',
    office: '',
    birthDate: data.dateOfBirth,
    gender: data.gender,
    ethnicity: data.ethnicity ?? 0,
    address: populateAddress(mailingData),
    bankId: bankData.bankId,
    accountHolder: bankData.accountHolder,
    accountNo: bankData.accountNo,
  };
};

const populateAddress = (data: AddressDetail) => {
  return {
    addressLine1: data.addressLine1?.includes('*') ? '' : data.addressLine1,
    addressLine2: data.addressLine2?.includes('*') ? '' : data.addressLine2,
    city: data.city?.includes('*') ? '' : data.city,
    postcode: data.postcode?.includes('*') ? '' : data.postcode,
    state: data.state?.includes('*') ? '' : data.state,
    country: data.country?.includes('*') ? '' : data.country,
  };
};
